import { useEffect, useState } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import { ChevronRight } from 'lucide-react'
import { api, type Stats, type Feedback, type ProjectWithStats } from '../lib/api'
import { Spinner, Empty, timeAgo } from '../components/ui'

const TAG_BADGE: Record<string, { bg: string; color: string }> = {
  bug:         { bg: '#fef2f2', color: '#dc2626' },
  feature:     { bg: '#eef2ff', color: '#4338ca' },
  praise:      { bg: '#f0fdf4', color: '#16a34a' },
  performance: { bg: '#fff7ed', color: '#c2410c' },
  other:       { bg: '#f1f5f9', color: '#475569' },
}

function StarRow({ rating }: { rating: number | null }) {
  if (!rating) return <span style={{ color: '#c7c4d8', fontSize: 14 }}>—</span>
  return (
    <div style={{ display: 'flex', gap: 1 }}>
      {[1,2,3,4,5].map(i => (
        <span key={i} style={{ color: '#f59e0b', fontSize: 14 }}>{i <= rating ? '★' : '☆'}</span>
      ))}
    </div>
  )
}

export function Overview() {
  const [searchParams] = useSearchParams()
  const projectId = searchParams.get('project')

  const [projects, setProjects] = useState<ProjectWithStats[]>([])
  const [stats, setStats] = useState<Stats | null>(null)
  const [recent, setRecent] = useState<Feedback[]>([])
  const [loading, setLoading] = useState(true)
  const [activeProject, setActiveProject] = useState<ProjectWithStats | null>(null)

  useEffect(() => {
    api.projects.list().then(({ projects: p }) => {
      setProjects(p)
      const proj = projectId ? p.find(x => x.id === projectId) : p[0]
      if (proj) setActiveProject(proj)
    })
  }, [projectId])

  useEffect(() => {
    if (!activeProject) return
    setLoading(true)
    Promise.all([
      api.feedback.stats(activeProject.id),
      api.feedback.list(activeProject.id, { limit: '5' }),
    ]).then(([s, f]) => {
      setStats(s)
      setRecent(f.data)
    }).finally(() => setLoading(false))
  }, [activeProject])

  const STAT_CARDS = stats ? [
    {
      icon: '💬', label: 'Total Feedback', value: stats.totals.total.toLocaleString(),
      badge: 'LIFETIME', badgeBg: '#f1f5f9', badgeColor: '#94a3b8',
      trend: null, iconBg: 'rgba(53,37,205,0.05)', iconColor: '#3525cd',
    },
    {
      icon: '⚡', label: 'New Today', value: `+${stats.totals.today_count ?? 0}`,
      badge: 'ACTIVE', badgeBg: '#eef2ff', badgeColor: '#4338ca',
      trend: (stats.totals.today_count ?? 0) > 0 ? { text: '↑ High', color: '#16a34a' } : null, iconBg: 'rgba(53,37,205,0.05)', iconColor: '#3525cd',
    },
    {
      icon: '⏳', label: 'Open Issues', value: stats.totals.open_count ?? 0,
      badge: 'PRIORITY', badgeBg: '#fff7ed', badgeColor: '#7e3000',
      trend: { text: 'Needs attention', color: '#7e3000' }, iconBg: 'rgba(126,48,0,0.05)', iconColor: '#7e3000',
    },
    {
      icon: '⭐', label: 'Avg Rating', value: stats.totals.avg_rating ? `${stats.totals.avg_rating}` : '—',
      badge: 'GLOBAL', badgeBg: '#f1f5f9', badgeColor: '#94a3b8',
      trend: null, iconBg: '#eef2ff', iconColor: '#4338ca',
    },
  ] : []

  const DOT_COLORS = ['#ba1a1a', '#7e3000', '#3525cd', '#c7c4d8']

  return (
    <div style={{ padding: '32px 48px 48px', maxWidth: 1400, margin: '0 auto' }}>

      {/* Hero header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 32 }}>
        <div>
          <h2 style={{ fontSize: 30, fontWeight: 800, letterSpacing: '-0.5px', color: '#191c1e', fontFamily: 'Manrope, sans-serif', margin: 0 }}>Overview</h2>
          <p style={{ color: '#464555', marginTop: 4, fontSize: 14, margin: '4px 0 0' }}>
            {activeProject ? `Real-time performance metrics for ${activeProject.name}.` : 'Select a project to view metrics.'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <select
            value={activeProject?.id ?? ''}
            onChange={e => setActiveProject(projects.find(p => p.id === e.target.value) ?? null)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 16px', background: '#ffffff', border: '1px solid rgba(199,196,216,0.3)',
              borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#191c1e',
              boxShadow: '0 1px 2px rgba(25,28,30,0.05)', cursor: 'pointer',
              fontFamily: 'Inter, sans-serif', outline: 'none',
            }}
          >
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <button style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 16px', background: '#3525cd', color: '#fff',
            border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600,
            cursor: 'pointer', boxShadow: '0 4px 12px rgba(53,37,205,0.25)',
          }}>
            ↓ Export Report
          </button>
        </div>
      </div>

      {loading ? <Spinner /> : !stats ? (
        <Empty icon="📊" title="No project selected" sub="Create a project to see your stats" />
      ) : (
        <>
          {/* Stat cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 24, marginBottom: 32 }}>
            {STAT_CARDS.map((card, i) => (
              <div key={i} style={{
                background: '#ffffff', padding: 24, borderRadius: 12,
                border: '1px solid rgba(199,196,216,0.15)',
                boxShadow: '0 1px 3px rgba(25,28,30,0.07)',
                transition: 'transform 0.2s', cursor: 'default',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 10, background: card.iconBg,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
                  }}>{card.icon}</div>
                  <span style={{
                    fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
                    background: card.badgeBg, color: card.badgeColor, padding: '3px 8px', borderRadius: 999,
                  }}>{card.badge}</span>
                </div>
                <p style={{ fontSize: 13, fontWeight: 500, color: '#464555', margin: '0 0 4px' }}>{card.label}</p>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <h3 style={{ fontSize: 26, fontWeight: 800, color: '#191c1e', fontFamily: 'Manrope, sans-serif', margin: 0 }}>{card.value}</h3>
                  {card.trend && <span style={{ fontSize: 12, fontWeight: 700, color: card.trend.color }}>{card.trend.text}</span>}
                </div>
              </div>
            ))}
          </div>

          {/* Chart + Top Issues */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24, marginBottom: 32 }}>
            {/* Chart */}
            <div style={{
              background: '#ffffff', padding: 32, borderRadius: 16,
              border: '1px solid rgba(199,196,216,0.15)',
              boxShadow: '0 1px 3px rgba(25,28,30,0.07)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 }}>
                <div>
                  <h3 style={{ fontSize: 16, fontWeight: 700, color: '#191c1e', fontFamily: 'Manrope, sans-serif', margin: '0 0 4px' }}>Feedback Over Time</h3>
                  <p style={{ fontSize: 13, color: '#464555', margin: 0 }}>Daily volume trend for the current billing cycle.</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#3525cd', display: 'inline-block' }} />
                    <span style={{ fontSize: 10, fontWeight: 700, color: '#777587', textTransform: 'uppercase' }}>Volume</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#e2e8f0', display: 'inline-block' }} />
                    <span style={{ fontSize: 10, fontWeight: 700, color: '#777587', textTransform: 'uppercase' }}>Average</span>
                  </div>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={stats.daily}>
                  <XAxis dataKey="date" tick={{ fill: '#777587', fontSize: 10, fontWeight: 700 }} tickFormatter={d => d.slice(5)} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#777587', fontSize: 10 }} axisLine={false} tickLine={false} width={28} />
                  <Tooltip
                    contentStyle={{ background: '#ffffff', border: 'none', borderRadius: 10, boxShadow: '0 8px 24px rgba(25,28,30,0.12)', padding: '8px 14px' }}
                    labelStyle={{ color: '#464555', fontSize: 11, fontWeight: 600 }}
                    itemStyle={{ color: '#3525cd', fontSize: 12 }}
                  />
                  <Line type="monotone" dataKey="count" stroke="#3525cd" strokeWidth={2.5} dot={false} activeDot={{ r: 5, fill: '#3525cd', strokeWidth: 0 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Top Issues */}
            <div style={{
              background: '#ffffff', padding: 32, borderRadius: 16,
              border: '1px solid rgba(199,196,216,0.15)',
              boxShadow: '0 1px 3px rgba(25,28,30,0.07)',
              display: 'flex', flexDirection: 'column',
            }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: '#191c1e', fontFamily: 'Manrope, sans-serif', margin: '0 0 24px' }}>Top Issues</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
                {stats.topIssues.length === 0 ? (
                  <p style={{ color: '#777587', fontSize: 13 }}>No open issues</p>
                ) : stats.topIssues.map((issue, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '14px 16px', background: '#f2f4f6', borderRadius: 12,
                    cursor: 'pointer', transition: 'background 0.15s',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: DOT_COLORS[i % DOT_COLORS.length], flexShrink: 0, display: 'inline-block' }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#191c1e' }}>
                        {issue.message.slice(0, 35)}{issue.message.length > 35 ? '…' : ''}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#464555' }}>{issue.count}</span>
                      <ChevronRight size={14} color="#c7c4d8" />
                    </div>
                  </div>
                ))}
              </div>
              <Link to={`/dashboard/feedback?project=${activeProject?.id}`} style={{
                display: 'block', textAlign: 'center', marginTop: 20,
                padding: '10px', fontSize: 12, fontWeight: 700, color: '#3525cd',
                border: '1px solid rgba(53,37,205,0.2)', borderRadius: 8,
                textDecoration: 'none', transition: 'background 0.15s',
              }}>View All Trends</Link>
            </div>
          </div>

          {/* Recent Feedback */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: 20, fontWeight: 700, color: '#191c1e', fontFamily: 'Manrope, sans-serif', margin: 0 }}>Recent Feedback</h3>
              <Link to={`/dashboard/feedback?project=${activeProject?.id}`} style={{
                fontSize: 13, fontWeight: 600, color: '#3525cd', textDecoration: 'none',
                display: 'flex', alignItems: 'center', gap: 4,
              }}>
                Explore all logs <span style={{ fontSize: 14 }}>↗</span>
              </Link>
            </div>

            <div style={{
              background: '#ffffff', borderRadius: 16, overflow: 'hidden',
              border: '1px solid rgba(199,196,216,0.15)',
              boxShadow: '0 1px 3px rgba(25,28,30,0.07)',
            }}>
              {recent.length === 0 ? (
                <Empty icon="💬" title="No feedback yet" sub="Install the SDK to start receiving feedback" />
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'rgba(242,244,246,0.6)' }}>
                      {['Rating', 'Message', 'Tag', 'Version', 'Time'].map(h => (
                        <th key={h} style={{ padding: '16px 24px', fontSize: 10, fontWeight: 700, color: '#777587', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {recent.map((f, i) => {
                      const tagStyle = TAG_BADGE[f.tag?.toLowerCase() ?? 'other'] ?? TAG_BADGE['other']!
                      return (
                        <tr key={f.id} style={{ borderTop: i > 0 ? '1px solid #f8f9fa' : 'none', transition: 'background 0.1s' }}>
                          <td style={{ padding: '20px 24px' }}><StarRow rating={f.rating} /></td>
                          <td style={{ padding: '20px 24px', maxWidth: 360 }}>
                            <p style={{ fontSize: 13, fontWeight: 500, color: '#191c1e', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {f.message}
                            </p>
                          </td>
                          <td style={{ padding: '20px 24px' }}>
                            {f.tag ? (
                              <span style={{ padding: '4px 12px', background: tagStyle.bg, color: tagStyle.color, fontSize: 10, fontWeight: 700, borderRadius: 999, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                {f.tag}
                              </span>
                            ) : <span style={{ color: '#c7c4d8' }}>—</span>}
                          </td>
                          <td style={{ padding: '20px 24px' }}>
                            {f.app_version ? (
                              <span style={{ fontSize: 12, fontFamily: 'monospace', color: '#464555', background: '#eceef0', padding: '2px 8px', borderRadius: 4 }}>
                                {f.app_version}
                              </span>
                            ) : <span style={{ color: '#c7c4d8', fontSize: 13 }}>—</span>}
                          </td>
                          <td style={{ padding: '20px 24px', textAlign: 'right', fontSize: 12, fontWeight: 500, color: '#777587', whiteSpace: 'nowrap' }}>
                            {timeAgo(f.created_at)}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
